import { PropsWithChildren, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { ArrowDown2, ExportSquare, Maximize4, Menu, SearchNormal1 } from 'iconsax-reactjs';

import { getRuntimeConfig, VendiaRuntimeUser } from '../runtime';
import { canAny, isSuperUser } from '../auth/permissions';
import { apiErrorMessage, fetchSettings } from '../api/client';
import { resetLocationState } from '../session/location';
import { t, getLocale, setLocale, subscribeLocale, availableLocales } from '../i18n';
import { BrandLogo } from '../brand';
import { useAuth } from './AuthGate';
import { buildNavSections, NavItem } from '../features/shell/nav';
import { ShellUser, stopImpersonation, syncServerLanguage } from '../features/shell/api';
import { ShortcutTarget, toggleFullscreen, useGlobalShortcuts } from '../features/shell/shortcuts';
import { startRealtime } from '../features/shell/realtime';
import { AlertsBell, OPEN_ALERTS_EVENT } from '../features/shell/header/AlertsBell';
import { LocationSwitcher } from '../features/shell/header/LocationSwitcher';
import { UserMenu } from '../features/shell/header/UserMenu';
import { EnvironmentChip } from '../features/shell/header/EnvironmentChip';
import { ShortcutsDialog } from '../features/shell/header/ShortcutsDialog';
import { CommandPalette } from '../features/shell/header/CommandPalette';
import { UI } from '../brand/ui';

const drawerWidth = 260;

function itemMatches(item: NavItem, currentPath: string) {
  const seg = item.to === '.' ? '' : item.to;
  return currentPath === seg || currentPath.endsWith('/' + seg);
}

/** Absolute URL of an in-app route, honouring the router basename. */
function appHref(to: string) {
  const base = getRuntimeConfig().basePath.replace(/\/$/, '');
  return `${base}/${to.replace(/^\//, '')}`;
}

export function AppShell({
  children,
  user,
  onLogout
}: PropsWithChildren<{ user: VendiaRuntimeUser | null; onLogout: () => void }>) {
  const runtime = getRuntimeConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const activeUser = (user ?? runtime.user) as ShellUser | null;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  // Restaurant mode gates visibility of the Recipes nav item (legacy restaurant: true route meta).
  const [restaurantMode, setRestaurantMode] = useState(false);
  // Re-render when the locale changes so all nav labels update instantly.
  const locale = useSyncExternalStore(subscribeLocale, getLocale, getLocale);

  useEffect(() => {
    fetchSettings()
      .then((data) => setRestaurantMode(Boolean(Number(data?.settings?.restaurant ?? 0))))
      .catch(() => setRestaurantMode(false));
    startRealtime();
  }, []);

  // First visit: follow the user's server-side locale when nothing was chosen locally.
  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem('vendia-locale');
    } catch {
      /* ignore */
    }
    if (!stored && activeUser?.locale && availableLocales.some((l) => l.code === activeUser.locale)) setLocale(activeUser.locale);
  }, [activeUser?.locale]);

  const isPortal = Boolean(activeUser?.customer_id || activeUser?.supplier_id);
  const sections = useMemo(
    () =>
      buildNavSections()
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => {
            if (item.superOnly && !isSuperUser(activeUser)) return false;
            if (item.restaurantOnly && !restaurantMode) return false;
            if (item.portalOnly && !isPortal) return false;
            return !item.permissions || canAny(activeUser, item.permissions);
          })
        }))
        .filter((section) => section.items.length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeUser, restaurantMode, isPortal, locale]
  );
  const navItems = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  // The section containing the current route auto-expands.
  const currentPath = location.pathname.replace(/\/$/, '');
  const activeSectionId = sections.find((section) => section.items.some((item) => itemMatches(item, currentPath)))?.id;
  const [expanded, setExpanded] = useState<string | false>(activeSectionId ?? false);
  useEffect(() => {
    setExpanded(activeSectionId ?? false);
  }, [activeSectionId]);

  const handleNavClick = () => {
    if (!isDesktop) setMobileOpen(false);
  };

  const handleLogout = useCallback(() => {
    resetLocationState();
    onLogout();
  }, [onLogout]);

  const handleStopImpersonation = useCallback(async () => {
    try {
      await stopImpersonation();
      resetLocationState();
      await refreshSession();
    } catch (error) {
      setToast({ msg: apiErrorMessage(error, t('common.actionFailed')), sev: 'error' });
    }
  }, [refreshSession]);

  const handleShortcut = useCallback(
    (shortcut: ShortcutTarget | 'palette') => {
      if (shortcut === 'palette') return setPaletteOpen((open) => !open);
      if (shortcut.action === 'help') return setShortcutsOpen((open) => !open);
      if (shortcut.action === 'alerts') return void window.dispatchEvent(new Event(OPEN_ALERTS_EVENT));
      if (shortcut.action === 'fullscreen') return toggleFullscreen();
      if (shortcut.action === 'pos') return void window.open(appHref('pos'), '_blank', 'noopener');
      if (shortcut.to !== undefined) navigate(`/${shortcut.to}`);
    },
    [navigate]
  );
  useGlobalShortcuts(handleShortcut);

  const changeLanguage = (code: string) => {
    setLocale(code);
    void syncServerLanguage(code);
  };

  const navButtonSx = {
    justifyContent: 'flex-start',
    color: 'text.secondary',
    borderRadius: '8px',
    textTransform: 'none',
    bgcolor: 'transparent',
    pl: 3.5,
    minHeight: 36,
    '&:hover': { bgcolor: UI.accentAlpha(0.07), color: 'primary.main' },
    '&.active': {
      bgcolor: UI.accentAlpha(0.12),
      color: 'primary.main',
      fontWeight: 700,
      boxShadow: 'inset 3px 0 0 currentColor',
      '&:hover': { bgcolor: UI.accentAlpha(0.16) },
      '& .MuiButton-startIcon': { color: 'primary.main' }
    }
  } as const;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {!isDesktop ? (
        <IconButton
          onClick={() => setMobileOpen(true)}
          aria-label={t('shell.openMenu')}
          sx={{
            position: 'fixed',
            top: 12,
            left: 12,
            zIndex: 1300,
            bgcolor: UI.white,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: UI.shadowSm
          }}
        >
          <Menu size={22} />
        </IconButton>
      ) : null}
      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop ? true : mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: `1px solid ${UI.border}`,
            bgcolor: UI.white,
            boxShadow: UI.shadowSm
          }
        }}
      >
        <Toolbar sx={{ minHeight: 74, bgcolor: 'transparent', px: 2.25 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <BrandLogo variant="horizontal" height={32} />
          </Stack>
        </Toolbar>
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            {runtime.appSubtitle}
          </Typography>
        </Box>
        <Divider sx={{ borderColor: UI.border }} />
        <Stack spacing={0.5} sx={{ p: 1.25 }}>
          {sections.map((section) => {
            const isExpanded = expanded === section.id;
            const hasActiveItem = section.items.some((item) => itemMatches(item, currentPath));
            return (
              <Accordion
                key={section.id}
                expanded={isExpanded}
                onChange={(_, isOpen) => setExpanded(isOpen ? section.id : false)}
                elevation={0}
                disableGutters
                square
                sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
              >
                <AccordionSummary
                  expandIcon={<ArrowDown2 size={16} />}
                  sx={{
                    minHeight: 40,
                    px: 1.5,
                    borderRadius: '8px',
                    '& .MuiAccordionSummary-content': { my: 0.5 },
                    '& .MuiAccordionSummary-expandIconWrapper': { transition: 'transform 150ms', color: hasActiveItem ? 'primary.main' : 'text.secondary' },
                    bgcolor: hasActiveItem ? UI.accentAlpha(0.08) : 'transparent'
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: hasActiveItem ? 'primary.main' : 'text.secondary' }}
                  >
                    {section.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, pb: 0.5 }}>
                  <Stack spacing={0.25}>
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      if (item.newTab) {
                        return (
                          <Button
                            key={`${section.id}:${item.to}`}
                            component="a"
                            href={appHref(item.to)}
                            target="_blank"
                            rel="noopener"
                            onClick={handleNavClick}
                            startIcon={<Icon size={18} />}
                            endIcon={<ExportSquare size={14} />}
                            sx={navButtonSx}
                          >
                            {item.label}
                          </Button>
                        );
                      }
                      return (
                        <Button
                          key={`${section.id}:${item.to}`}
                          component={NavLink}
                          to={item.to === '.' ? '/' : `/${item.to}`}
                          end={item.end}
                          onClick={handleNavClick}
                          startIcon={<Icon size={18} />}
                          sx={navButtonSx}
                        >
                          {item.label}
                        </Button>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      </Drawer>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          component="header"
          sx={{
            flexShrink: 0,
            minHeight: 78,
            px: 3,
            pl: { xs: 9, lg: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            bgcolor: UI.white,
            borderBottom: `1px solid ${UI.border}`,
            minWidth: 0
          }}
        >
          <Stack spacing={0.25} sx={{ minWidth: 0, flexShrink: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" noWrap>
              {runtime.appSubtitle}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }} noWrap>
              {runtime.appName}
            </Typography>
          </Stack>
          <Box
            component="button"
            type="button"
            data-testid="command-search"
            onClick={() => setPaletteOpen(true)}
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 1,
              flex: '0 1 420px',
              minWidth: 200,
              px: 1.5,
              py: 1,
              borderRadius: UI.radiusMd,
              bgcolor: UI.white,
              border: `1px solid ${UI.border}`,
              color: 'text.secondary',
              boxShadow: UI.shadowSm,
              cursor: 'pointer',
              font: 'inherit',
              textAlign: 'left'
            }}
          >
            <SearchNormal1 size={17} />
            <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }} noWrap>
              {t('common.commandSearch')}
            </Typography>
            <Chip size="small" label="⌘K" variant="outlined" sx={{ height: 22, fontSize: 11, bgcolor: UI.accentAlpha(0.06) }} />
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: 1 }}>
            {isSuperUser(activeUser) ? <EnvironmentChip /> : null}
            <LocationSwitcher user={activeUser} />
            <AlertsBell />
            <Tooltip title={t('shell.fullscreen')}>
              <IconButton onClick={toggleFullscreen} aria-label={t('shell.fullscreen')} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                <Maximize4 size={20} />
              </IconButton>
            </Tooltip>
            <TextField
              select
              size="small"
              value={locale}
              onChange={(e) => changeLanguage(e.target.value)}
              inputProps={{ 'aria-label': t('shell.language') }}
              sx={{ minWidth: 80, '& .MuiSelect-select': { py: 0.75, fontSize: '0.85rem' } }}
              SelectProps={{ renderValue: (val) => availableLocales.find((l) => l.code === val)?.flag + ' ' + String(val).toUpperCase() }}
            >
              {availableLocales.map((loc) => (
                <MenuItem key={loc.code} value={loc.code}>
                  {loc.flag} {loc.label}
                </MenuItem>
              ))}
            </TextField>
            <UserMenu
              user={activeUser}
              onLogout={handleLogout}
              onStopImpersonation={() => void handleStopImpersonation()}
              onShowShortcuts={() => setShortcutsOpen(true)}
            />
          </Stack>
        </Box>
        {activeUser?.acting_user ? (
          <Alert
            severity="warning"
            data-testid="impersonation-banner"
            sx={{ borderRadius: 0 }}
            action={
              <Button color="inherit" size="small" onClick={() => void handleStopImpersonation()}>
                {t('shell.impersonation.stop')}
              </Button>
            }
          >
            {t('shell.impersonation.actingAs', { name: activeUser.acting_user.name ?? activeUser.acting_user.username ?? '' })}
          </Alert>
        ) : null}
        <Box component="main" sx={{ p: { xs: 2, lg: 3 }, flex: 1, overflowX: 'hidden', overflowY: 'auto', minWidth: 0 }}>
          {children}
        </Box>
      </Box>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} navItems={navItems} user={activeUser} />
      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity={toast.sev} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
